"use client";

import { useEffect } from "react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import type { HallOfFameCategory } from "@/api/query/hall-of-fame";
import { useHallOfFamePicture } from "@/api/hook/useHallOfFame";

interface HallOfFameCategoryCardProps {
  category: HallOfFameCategory;
  colorIndex: number;
}

const CATEGORY_COLORS = [
  {
    gradient: "from-[#21205A] via-[#3d3b8e] to-[#8253C3]",
    icon: "🏆",
    border: "border-[#8253C3]/20",
  },
  {
    gradient: "from-[#0F6E56] via-[#1D9E75] to-[#5DCAA5]",
    icon: "🌟",
    border: "border-[#1D9E75]/20",
  },
  {
    gradient: "from-[#185FA5] via-[#378ADD] to-[#85B7EB]",
    icon: "🎓",
    border: "border-[#378ADD]/20",
  },
  {
    gradient: "from-[#993C1D] via-[#D85A30] to-[#F09975]",
    icon: "🔥",
    border: "border-[#D85A30]/20",
  },
  {
    gradient: "from-[#993556] via-[#D4537E] to-[#ED93B1]",
    icon: "💎",
    border: "border-[#D4537E]/20",
  },
  {
    gradient: "from-[#854F0B] via-[#BA7517] to-[#EF9F27]",
    icon: "⭐",
    border: "border-[#BA7517]/20",
  },
];

const DESCRIPTION_LINE_LIMIT = 3;

/**
 * Converts a YouTube watch/share URL into an embeddable URL.
 * Supports: youtube.com/watch?v=ID, youtu.be/ID, and already-embed URLs.
 * Returns null if the URL doesn't look like a YouTube link.
 */
function getYouTubeEmbedUrl(url?: string | null): string | null {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    let videoId: string | null = null;

    if (parsed.hostname.includes("youtu.be")) {
      videoId = parsed.pathname.slice(1);
    } else if (parsed.hostname.includes("youtube.com")) {
      if (parsed.pathname === "/watch") {
        videoId = parsed.searchParams.get("v");
      } else if (parsed.pathname.startsWith("/embed/")) {
        videoId = parsed.pathname.split("/embed/")[1];
      } else if (parsed.pathname.startsWith("/shorts/")) {
        videoId = parsed.pathname.split("/shorts/")[1];
      }
    }

    if (!videoId) return null;

    // strip any trailing path/query segments that might have leaked in
    videoId = videoId.split(/[?&/]/)[0];

    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
  } catch {
    return null;
  }
}

export const HallOfFameCategoryCard = ({
  category,
  colorIndex,
}: HallOfFameCategoryCardProps) => {
  const router = useRouter();
  const colors = CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length];
  const [expanded, setExpanded] = useState(false);

  const { data: pictureUrl } = useHallOfFamePicture(category.id);

  // Revoke the blob URL when it changes or the card unmounts, to avoid leaking memory
  useEffect(() => {
    return () => {
      if (pictureUrl) URL.revokeObjectURL(pictureUrl);
    };
  }, [pictureUrl]);

  const embedUrl = !pictureUrl ? getYouTubeEmbedUrl(category.videoUrl) : null;

  // Rough heuristic: >120 chars is likely more than 3 lines
  const isLong = (category.description?.length ?? 0) > 160;

  const handleCardClick = () => {
    router.push(`/hall-of-fame/detail?id=${category.id}`);
  };

  const handleSeeMore = (e: React.MouseEvent) => {
    e.stopPropagation(); // prevent card navigation
    setExpanded((prev) => !prev);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group cursor-pointer relative overflow-hidden rounded-2xl border ${colors.border} bg-white hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full`}
    >
      {/* Header — image if available, YouTube video if provided, gradient otherwise */}
      <div className="relative   h-60 flex-shrink-0 overflow-hidden">
        {pictureUrl ? (
          <>
            <img
              src={pictureUrl}
              alt={category.title}
              className="absolute inset-0 h-full w-full"
            />
            {/* subtle scrim so the icon/edges stay legible over any photo */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          </>
        ) : embedUrl ? (
          <iframe
            src={embedUrl}
            title={category.title}
            className="absolute inset-0 h-full w-full"
            frameBorder={0}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            // Stop the card's onClick navigation from firing when the user
            // is interacting with the player (play/pause, seek, etc.)
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div
            className={`absolute inset-0 bg-gradient-to-br ${colors.gradient}`}
          />
        )}

        <div className="absolute top-4 right-4 text-5xl opacity-30 group-hover:opacity-50 transition-opacity duration-300 select-none pointer-events-none">
          {colors.icon}
        </div>
        {!pictureUrl && !embedUrl && (
          <div className="absolute -bottom-0 right-0 w-32 h-32 rounded-tl-full opacity-10 bg-white" />
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-bold text-gray-900 text-base md:text-lg leading-snug line-clamp-2 mb-3 group-hover:text-primary transition-colors">
          {category.title}
        </h3>

        {category.description && (
          <div className="mb-4">
            <p
              className={`text-gray-600 text-sm ${!expanded && isLong ? "line-clamp-3" : ""}`}
            >
              {category.description}
            </p>
            {isLong && (
              <button
                onClick={handleSeeMore}
                className="text-xs font-semibold mt-1 text-primary hover:underline focus:outline-none"
              >
                {expanded ? "See less" : "See more"}
              </button>
            )}
          </div>
        )}

        {/* CTA */}
        <button
          className={`mt-auto w-full bg-gradient-to-r ${colors.gradient} text-white font-semibold py-2.5 rounded-xl text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
        >
          View Members
          <span className="group-hover:translate-x-1 transition-transform duration-200">
            →
          </span>
        </button>
      </div>
    </div>
  );
};
